/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IFilterParams } from 'ag-grid-community';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const cozonacApiUrl = process.env.REACT_APP_COZONAC_API_URL;
const DEBOUNCE_MS = 300;

interface Suggestion {
  name: string;
  count: number;
}

export interface DependencyNameFilterModel {
  filterType: 'set';
  values: string[];
}

export const DependencyNameFilter = forwardRef((params: IFilterParams, ref) => {
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const insideFilter = containerRef.current?.contains(e.target as Node);
      const insideDropdown = dropdownRef.current?.contains(e.target as Node);
      if (!insideFilter && !insideDropdown) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showDropdown && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    }
  }, [showDropdown]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortControllerRef.current?.abort();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    isFilterActive() {
      return selectedNames.length > 0;
    },
    doesFilterPass() {
      return true; // server-side model — not evaluated client-side
    },
    getModel(): DependencyNameFilterModel | null {
      if (selectedNames.length === 0) return null;
      return { filterType: 'set', values: selectedNames };
    },
    setModel(model: DependencyNameFilterModel | null) {
      setSelectedNames(model?.values ?? []);
    },
  }));

  const fetchSuggestions = useCallback(async (search: string) => {
    if (!search.trim()) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    try {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const token = await getAccessTokenSilently();

      // Include current grid filters (e.g. status) so suggestions only show names
      // that have records matching the active filters, preventing 0-result selections.
      // params.api from ag-grid v31 inside a custom filter does not reflect other
      // columns' models, so prefer the gridApi forwarded via context.
      const ctxApi = (params.context as { gridApiRef?: { current: { getFilterModel: () => Record<string, unknown> } | null } } | undefined)?.gridApiRef?.current;
      const filterApi = ctxApi ?? params.api;
      const otherGridFilters = Object.fromEntries(
        Object.entries(filterApi?.getFilterModel() ?? {}).filter(([key]) => key !== 'dependencyName')
      );

      const resp = await axios.post(
        `${cozonacApiUrl}/api/dependencies/dataSource`,
        {
          startRow: 0,
          endRow: 20,
          skipCount: true,
          rowGroupCols: [{ id: 'dependencyName', field: 'dependencyName', displayName: 'Dependency Name' }],
          groupKeys: [],
          filterModel: {
            ...otherGridFilters,
            dependencyName: { filterType: 'text', type: 'contains', filter: search },
          },
        },
        { headers: { Authorization: `bearer ${token}` }, signal: abortControllerRef.current.signal }
      );
      const currentSelected = selectedNames;
      const results: Suggestion[] = (resp.data.rowData ?? [])
        .map((row: any) => ({ name: row.dependencyName, count: row.metaData?.childCount }))
        .filter((s: Suggestion) => s.name && !currentSelected.includes(s.name));
      setSuggestions(results);
      setShowDropdown(results.length > 0);
    } catch {
      setSuggestions([]);
      setShowDropdown(false);
    }
  }, [getAccessTokenSilently, selectedNames, params.api, params.context]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), DEBOUNCE_MS);
  };

  const addName = (name: string) => {
    setSelectedNames(prev => prev.includes(name) ? prev : [...prev, name]);
    setInputValue('');
    setSuggestions([]);
    setShowDropdown(false);
  };

  const removeName = (name: string) => {
    setSelectedNames(prev => prev.filter(n => n !== name));
  };

  const onApply = () => {
    params.filterChangedCallback();
  };

  const onReset = () => {
    setSelectedNames([]);
    setInputValue('');
    setSuggestions([]);
    setShowDropdown(false);
    params.filterChangedCallback();
  };

  return (
    <div ref={containerRef} style={{ padding: 8, minWidth: 240, fontFamily: 'inherit', fontSize: 13 }}>
      {selectedNames.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
          {selectedNames.map(name => (
            <span
              key={name}
              style={{
                background: '#e0e0e0', borderRadius: 12, padding: '2px 8px',
                fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              {name}
              <button
                type="button"
                onMouseDown={() => removeName(name)}
                aria-label={`Remove ${name}`}
                style={{ cursor: 'pointer', fontWeight: 'bold', lineHeight: 1, background: 'none', border: 'none', padding: 0 }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        ref={inputRef}
        value={inputValue}
        onChange={onInputChange}
        placeholder="Type to search..."
        style={{
          width: '100%', padding: '4px 6px', boxSizing: 'border-box',
          border: '1px solid #ccc', borderRadius: 3, fontSize: 13,
        }}
      />
      {showDropdown && dropdownPos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed', zIndex: 99999,
            top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width,
            background: '#fff', border: '1px solid #ccc', borderRadius: 3,
            maxHeight: 200, overflowY: 'auto', boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          }}
        >
          {suggestions.map(s => (
            <button
              key={s.name}
              type="button"
              onMouseDown={() => addName(s.name)}
              style={{
                width: '100%', padding: '6px 8px', cursor: 'pointer',
                display: 'flex', justifyContent: 'space-between',
                background: 'none', border: 'none', textAlign: 'left',
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#f5f5f5')}
              onMouseOut={e => (e.currentTarget.style.background = '')}
            >
              <span>{s.name}</span>
              <span style={{ color: '#888', fontSize: 11, marginLeft: 8 }}>{s.count}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button onClick={onApply} style={{ flex: 1, padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}>
          Apply
        </button>
        <button onClick={onReset} style={{ flex: 1, padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}>
          Reset
        </button>
      </div>
    </div>
  );
});

DependencyNameFilter.displayName = 'DependencyNameFilter';
